import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { logout } from "../auth/helper";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { collapsedDrawerWidth, drawerWidth } from "../AppBarWithDrawer";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FormatListNumberedRtlIcon from "@mui/icons-material/FormatListNumberedRtl";
import { useDispatch, useSelector } from "react-redux";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import ReviewsIcon from "@mui/icons-material/Reviews";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import DiscountIcon from "@mui/icons-material/Discount";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import LogoutIcon from "@mui/icons-material/Logout";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { get } from "lockr";
import { can, canAny, hasAnyFeature } from "../../auth/permissions";
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { RootState } from "../../redux/store";

type MenuItem = {
  title: string;
  icon: JSX.Element;
  route?: string;
  requiredPermissions?: string[];
  requiredFeatures?: string[];
  children?: MenuItem[];
};

const navText = {
  color: "#7a6560",
  active: "#f0ece8",
  accent: "#9e4a50",
  border: "rgba(131, 60, 65, 0.18)",
  card: "#16100f",
  secondary: "#1f1615",
  primary: "#833c41",
};

const baseMenuList: MenuItem[] = [
  {
    title: "Dashboard",
    icon: <DashboardIcon />,
    route: "dashboard",
    requiredPermissions: ["dashboard_view"],
    requiredFeatures: ["segment_map", "analytics"],
  },
  {
    title: "Product",
    icon: <Inventory2Icon />,
    requiredFeatures: ["menu_management"],
    children: [
      {
        title: "New Product",
        icon: <AddIcon />,
        route: "add-product",
        requiredPermissions: ["product_write"],
        requiredFeatures: ["menu_management"],
      },
      {
        title: "Drinks",
        icon: <LocalBarIcon />,
        route: "drinks",
        requiredPermissions: ["product_view"],
        requiredFeatures: ["menu_management"],
      },
      {
        title: "Food",
        icon: <RestaurantIcon />,
        route: "food",
        requiredPermissions: ["product_view"],
        requiredFeatures: ["menu_management"],
      },
      {
        title: "Combo",
        icon: <FastfoodIcon />,
        route: "combo",
        requiredPermissions: ["product_view"],
        requiredFeatures: ["menu_management"],
      },
    ],
  },
  {
    title: "Orders",
    icon: <FormatListNumberedRtlIcon />,
    route: "order",
    requiredPermissions: ["order_view"],
    requiredFeatures: ["orders"],
  },
  {
    title: "Promotions",
    icon: <DiscountIcon />,
    requiredFeatures: ["promotions"],
    children: [
      {
        title: "Campaign",
        icon: <DiscountIcon />,
        route: "campaign",
        requiredPermissions: ["promotion_view"],
        requiredFeatures: ["promotions"],
      },
      {
        title: "Discounts",
        icon: <DiscountIcon />,
        route: "promotions/discounts",
        requiredPermissions: ["promotion_view"],
        requiredFeatures: ["promotions"],
      },
    ],
  },
  {
    title: "Feedback",
    icon: <ReviewsIcon />,
    route: "feedback",
    requiredFeatures: ["feedback"],
  },
  {
    title: "Staff Management",
    icon: <PeopleIcon />,
    route: "staff-management",
    requiredPermissions: ["staff_view"],
    requiredFeatures: ["staff_management"],
  },
  {
    title: "Settings",
    icon: <SettingsIcon />,
    route: "settings",
    requiredPermissions: ["account_settings"],
  },
];

const getStaffMenuList = (currentMenu: MenuItem[]): MenuItem[] => {
  return currentMenu
    .map((menu) => {
      if (!menu.children) return menu;

      return {
        ...menu,
        children: menu.children.filter((child) =>
          canAny(child.requiredPermissions) && hasAnyFeature(child.requiredFeatures)
        ),
      };
    })
    .filter(
      (menu) =>
        canAny(menu.requiredPermissions) &&
        hasAnyFeature(menu.requiredFeatures) &&
        (!menu.children || menu.children.length > 0)
    );
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: collapsedDrawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
  "& .MuiDrawer-paper": {
    borderRight: `1px solid ${navText.border}`,
    background: navText.card,
    color: "#f0ece8",
  },
}));

interface Props {
  open: boolean;
  handleDrawerClose: () => void;
}

export const SideDrawer = (props: Props) => {
  const logo = require("../../assets/logo-crop.png");
  const { open, handleDrawerClose } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const staffPermissions = get("staff-permissions") as string[] | undefined;
  const user = useSelector((state: RootState) => state.Auth.user);
  const unseenOrders = useSelector(
    (state: RootState) => state.OrderNotification.unseenOrderIds.length
  );
  const { packages } = useSelector((state: RootState) => state.Package);
  const combos = useSelector((state: RootState) => state.Combo.combos);
  const canManageCombos = can("product_write");
  const canManageCampaigns = can("promotion_write");
  const [productOpen, setProductOpen] = useState(
    /\/app\/(add-product|products\/bulk|drinks|food|combo)/.test(location.pathname)
  );
  const [promotionsOpen, setPromotionsOpen] = useState(
    /\/app\/(campaign|promotions)/.test(location.pathname)
  );

  const isMenuOpen = (title: string) =>
    title === "Product" ? productOpen : promotionsOpen;

  const toggleMenu = (title: string) => {
    if (title === "Product") {
      setProductOpen((current) => !current);
      return;
    }
    setPromotionsOpen((current) => !current);
  };

  const comboRoute = useMemo(() => {
    return canManageCombos && combos && combos.length > 0
      ? "combo/management"
      : "combo";
  }, [canManageCombos, combos]);

  const campaignRoute = useMemo(() => {
    return canManageCampaigns && packages && packages.length > 0
      ? "campaign/management"
      : "campaign";
  }, [canManageCampaigns, packages]);

  const currentMenuList = useMemo(() => {
    const updateRoutes = (items: MenuItem[]): MenuItem[] =>
      items.map((item) => {
        const mappedItem = { ...item };
        if (mappedItem.title === "Campaign") mappedItem.route = campaignRoute;
        if (mappedItem.title === "Combo") mappedItem.route = comboRoute;
        if (mappedItem.children) mappedItem.children = updateRoutes(mappedItem.children);
        return mappedItem;
      });

    return updateRoutes(baseMenuList);
  }, [campaignRoute, comboRoute]);

  const staffFilteredList = useMemo(() => {
    return staffPermissions
      ? getStaffMenuList(currentMenuList)
      : currentMenuList;
  }, [currentMenuList, staffPermissions]);

  const productionDomain = "admin.menutender.com";
  const isProductionDomain = window.location.hostname === productionDomain;

  const filteredMenuList = useMemo(() => {
    const stripUnavailable = (items: MenuItem[]): MenuItem[] =>
      items
        .map((item) => {
          if (!item.children) return item;
          return {
            ...item,
            children: stripUnavailable(item.children),
          };
        })
        .filter((menu) => {
          const hiddenInProduction =
            isProductionDomain &&
            (menu.title === "Combo" || menu.title === "Campaign");
          return (
            !hiddenInProduction && (!menu.children || menu.children.length > 0)
          );
        });

    return stripUnavailable(staffFilteredList);
  }, [staffFilteredList, isProductionDomain]);

  const isRouteActive = (route?: string) => {
    if (!route) return false;
    const target = `/app/${route}`;
    return location.pathname === target || location.pathname.startsWith(`${target}/`);
  };

  const isGroupActive = (menu: MenuItem) => {
    return !!menu.children?.some((child) => isRouteActive(child.route));
  };

  const navigateToRoute = (route?: string) => {
    if (!route) return;
    const target = `/app/${route}`;
    if (location.pathname !== target) {
      navigate(target);
    }
  };

  const initials = user.legalName
    ? user.legalName
        .split(" ")
        .map((name) => name.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "AD";

  useEffect(() => {
    if (/\/app\/(add-product|drinks|food|combo)/.test(location.pathname)) {
      setProductOpen(true);
    }
  }, [location.pathname]);

  const renderNavButton = (
    menu: MenuItem,
    options: { nested?: boolean; active?: boolean } = {}
  ) => {
    const active = options.active ?? isRouteActive(menu.route);
    const content = (
      <Box
        component="button"
        type="button"
        onClick={() => navigateToRoute(menu.route)}
        sx={{
          width: "100%",
          minHeight: options.nested ? 38 : 42,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          gap: 1.25,
          border: 0,
          borderRadius: "7px",
          px: open ? (options.nested ? 1.25 : 1.5) : 0,
          color: active ? navText.active : navText.color,
          backgroundColor: active ? "rgba(131, 60, 65, 0.14)" : "transparent",
          cursor: "pointer",
          font: "inherit",
          textAlign: "left",
          transition: "background-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            backgroundColor: active
              ? "rgba(131, 60, 65, 0.18)"
              : "rgba(31, 22, 21, 0.88)",
            color: active ? navText.active : "#c8b8b0",
          },
          "& svg": {
            fontSize: options.nested ? 17 : 18,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            color: active ? navText.accent : "inherit",
          }}
        >
          {menu.title === "Orders" && unseenOrders ? (
            <Badge
              badgeContent={unseenOrders}
              color="error"
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: 9,
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              {menu.icon}
            </Badge>
          ) : (
            menu.icon
          )}
        </Box>
        {open && (
          <Typography
            component="span"
            sx={{
              flex: 1,
              overflow: "hidden",
              fontSize: options.nested ? "13px" : "13.5px",
              fontWeight: active ? 600 : 500,
              lineHeight: 1.2,
              textOverflow: "ellipsis",
            }}
          >
            {menu.title}
          </Typography>
        )}
        {open && menu.title === "Orders" && unseenOrders > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 22,
              height: 20,
              px: 0.7,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "999px",
              bgcolor: "#d32f2f",
              color: "#fff",
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {unseenOrders > 99 ? "99+" : unseenOrders}
          </Box>
        )}
      </Box>
    );

    if (open) return content;

    return (
      <Tooltip key={menu.title} title={menu.title} placement="right">
        {content}
      </Tooltip>
    );
  };

  return (
    <Drawer variant="permanent" open={open}>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            minHeight: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            gap: 1,
            borderBottom: `1px solid ${navText.border}`,
            px: open ? 2 : 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                borderRadius: "7px",
                backgroundColor: navText.primary,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Menutender Logo"
                sx={{ width: 28, height: 28, objectFit: "contain" }}
              />
            </Box>
            {open && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#f0ece8",
                    fontFamily: '"Cormorant Infant", Georgia, "Times New Roman", serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  Menutender
                </Typography>
                <Typography
                  sx={{
                    mt: "3px",
                    color: navText.color,
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "9px",
                    letterSpacing: "0.12em",
                    lineHeight: 1,
                    textTransform: "uppercase",
                  }}
                >
                  Admin console
                </Typography>
              </Box>
            )}
          </Box>
          {open && (
            <IconButton
              aria-label="close drawer"
              onClick={handleDrawerClose}
              sx={{
                width: 30,
                height: 30,
                color: navText.color,
                "&:hover": {
                  color: "#f9f3ef",
                  backgroundColor: "rgba(131, 60, 65, 0.12)",
                },
              }}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ p: open ? "10px 16px" : "10px 8px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "flex-start" : "center",
              gap: 0.75,
              borderRadius: "7px",
              p: open ? "7px 10px" : "8px 0",
              backgroundColor: navText.secondary,
              color: "#c8b8b0",
            }}
          >
            <StorefrontIcon sx={{ fontSize: 14, color: navText.color }} />
            {open && (
              <Typography
                sx={{
                  overflow: "hidden",
                  fontSize: "12px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user.legalName || "Main location"}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          component="nav"
          className="navigate mobile-navigate"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            overflowY: "auto",
            px: open ? "10px" : "8px",
            pb: 2,
          }}
        >
          {filteredMenuList.map((menu) => {
            if (menu.children) {
              const groupActive = isGroupActive(menu);
              return (
                <Box key={menu.title}>
                  <Tooltip title={open ? "" : menu.title} placement="right">
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleMenu(menu.title)}
                      sx={{
                        width: "100%",
                        minHeight: 42,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: open ? "flex-start" : "center",
                        gap: 1.25,
                        border: 0,
                        borderRadius: "7px",
                        px: open ? 1.5 : 0,
                        color: groupActive ? navText.active : navText.color,
                        backgroundColor: groupActive
                          ? "rgba(131, 60, 65, 0.14)"
                          : "transparent",
                        cursor: "pointer",
                        font: "inherit",
                        textAlign: "left",
                        "&:hover": {
                          backgroundColor: groupActive
                            ? "rgba(131, 60, 65, 0.18)"
                            : "rgba(31, 22, 21, 0.88)",
                          color: groupActive ? navText.active : "#c8b8b0",
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          color: groupActive ? navText.accent : "inherit",
                          "& svg": { fontSize: 18 },
                        }}
                      >
                        {menu.icon}
                      </Box>
                      {open && (
                        <>
                          <Typography
                            component="span"
                            sx={{
                              flex: 1,
                              fontSize: "13.5px",
                              fontWeight: groupActive ? 600 : 500,
                              lineHeight: 1.2,
                            }}
                          >
                            {menu.title}
                          </Typography>
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: 16,
                              transform: isMenuOpen(menu.title)
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </Tooltip>
                  {open && (
                    <Collapse in={isMenuOpen(menu.title)} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          mt: "3px",
                          pl: 1.6,
                        }}
                      >
                        {menu.children.map((child) => (
                          <Box key={child.route || child.title}>
                            {renderNavButton(child, {
                              nested: true,
                              active: isRouteActive(child.route),
                            })}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            }

            return (
              <Box key={menu.route || menu.title}>
                {renderNavButton(menu)}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${navText.border}`,
            p: open ? "12px 14px" : "12px 8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "flex-start" : "center",
              gap: 1.2,
              minWidth: 0,
              mb: 1,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: navText.primary,
                color: "#f9f3ef",
                fontFamily:
                  '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                fontSize: "11px",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
            {open && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    overflow: "hidden",
                    color: "#f0ece8",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.legalName || "Admin User"}
                </Typography>
                <Typography
                  sx={{
                    overflow: "hidden",
                    color: navText.color,
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "10px",
                    lineHeight: 1.35,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {staffPermissions ? "Staff account" : "Super admin"}
                </Typography>
              </Box>
            )}
          </Box>
          <Tooltip title={open ? "" : "Logout"} placement="right">
            <Box
              component="button"
              type="button"
              onClick={() => logout(dispatch)}
              sx={{
                width: "100%",
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: open ? "flex-start" : "center",
                gap: 1,
                border: 0,
                borderRadius: "7px",
                px: open ? 1.5 : 0,
                color: navText.color,
                backgroundColor: "transparent",
                cursor: "pointer",
                font: "inherit",
                "&:hover": {
                  color: "#f9f3ef",
                  backgroundColor: "rgba(131, 60, 65, 0.14)",
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              {open && (
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                  Logout
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};
